import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import {
  FolderOpen,
  FolderClosed,
  FileText,
  Search,
  ChevronRight,
  ChevronDown,
  ChevronLeft,
  ChevronUp,
  RefreshCw,
  Download,
  Printer,
  Maximize2,
  ExternalLink,
  ZoomIn,
  ZoomOut,
  X,
  Eye,
  Minus,
  Plus,
  User,
} from "lucide-react";
import { fetchDocuments, fetchAttachmentDetail } from "../../services/documentsApi";
import "./DocumentFileViewer.css";

const ZOOM_LEVELS = [50, 75, 100, 125, 140, 160, 200];

const DocumentFileViewer = ({ loanId, getToken }) => {
  // Folder tree state
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [expandedFolders, setExpandedFolders] = useState({});
  const [showSearch, setShowSearch] = useState(false);
  const [sortBy, setSortBy] = useState("name-asc");
  const [showMyFiles, setShowMyFiles] = useState(false);
  const [currentUserId, setCurrentUserId] = useState("");

  // Fetch logged-in user id
  useEffect(() => {
    const fetchUserId = async () => {
      try {
        const authObj = await window.elli.script.getObject("auth");
        const user = await authObj.getUser();
        setCurrentUserId(user?.id || "");
      } catch (err) {
        console.error("Failed to get user id:", err);
      }
    };
    fetchUserId();
  }, []);

  // Selected file state
  const [selectedFileId, setSelectedFileId] = useState(null);
  const [selectedFileName, setSelectedFileName] = useState("");
  const [fileDetail, setFileDetail] = useState(null);
  const [fileLoading, setFileLoading] = useState(false);
  const [selectedPageCount, setSelectedPageCount] = useState(0);

  // Viewer state
  const [currentPage, setCurrentPage] = useState(1);
  const [zoomLevel, setZoomLevel] = useState(100);
  const [showThumbnails, setShowThumbnails] = useState(true);
  const [showDownloadMenu, setShowDownloadMenu] = useState(false);

  // Image blob URLs for pages and thumbnails
  const [pageImages, setPageImages] = useState({});
  const [thumbnailImages, setThumbnailImages] = useState({});

  const downloadMenuRef = useRef(null);
  const viewerRef = useRef(null);

  // Fetch folder list
  const fetchFolders = useCallback(async () => {
    if (!loanId) return;
    setLoading(true);
    try {
      const token = await getToken();
      const data = await fetchDocuments(loanId, token);
      setDocuments(data);
    } catch (error) {
      console.error("Failed to load documents:", error);
    } finally {
      setLoading(false);
    }
  }, [loanId, getToken]);

  useEffect(() => {
    fetchFolders();
  }, [fetchFolders]);

  // Derive page/thumbnail URLs from the base attachment URL
  // URL format: https://.../B2.guid?validity=...
  // Page URL: https://.../B2.guid.meta/transformation/pages/1.png?validity=...
  // Thumbnail URL: https://.../B2.guid.meta/transformation/thumbnails/1.png?validity=...
  const derivePageUrl = (baseUrl, pageNumber) => {
    const qIndex = baseUrl.indexOf("?validity");
    if (qIndex === -1) return null;
    const basePath = baseUrl.substring(0, qIndex);
    const queryString = baseUrl.substring(qIndex);
    return `${basePath}.meta/transformation/pages/${pageNumber}.png${queryString}`;
  };

  const deriveThumbnailUrl = (baseUrl, pageNumber) => {
    const qIndex = baseUrl.indexOf("?validity");
    if (qIndex === -1) return null;
    const basePath = baseUrl.substring(0, qIndex);
    const queryString = baseUrl.substring(qIndex);
    return `${basePath}.meta/transformation/thumbnails/${pageNumber}.png${queryString}`;
  };

  // Fetch attachment detail on file click
  const loadFileDetail = useCallback(async (attachmentId, pageCount) => {
    if (!loanId || !attachmentId) return;
    setFileLoading(true);
    setFileDetail(null);
    setPageImages({});
    setThumbnailImages({});
    setCurrentPage(1);
    setSelectedPageCount(pageCount || 0);
    try {
      const token = await getToken();
      const detail = await fetchAttachmentDetail(loanId, attachmentId, token);
      setFileDetail(detail);
      // Load thumbnails and first page using derived URLs
      if (detail.url && pageCount > 0) {
        loadThumbnails(detail.url, detail.authorizationHeader, pageCount);
        loadPageImage(detail.url, detail.authorizationHeader, 0);
      }
    } catch (error) {
      console.error("Failed to load file detail:", error);
    } finally {
      setFileLoading(false);
    }
  }, [loanId, getToken]);

  // Load thumbnail images
  const loadThumbnails = async (baseUrl, authHeader, pageCount) => {
    for (let i = 0; i < pageCount; i++) {
      const thumbUrl = deriveThumbnailUrl(baseUrl, i + 1);
      if (!thumbUrl) continue;
      try {
        const response = await fetch(thumbUrl, {
          headers: authHeader ? { Authorization: authHeader } : {},
        });
        if (response.ok) {
          const blob = await response.blob();
          const url = URL.createObjectURL(blob);
          setThumbnailImages((prev) => ({ ...prev, [i]: url }));
        }
      } catch (err) {
        // Thumbnail load failed silently
      }
    }
  };

  // Load a single page image
  const loadPageImage = async (baseUrl, authHeader, pageIndex) => {
    const pageUrl = derivePageUrl(baseUrl, pageIndex + 1);
    if (!pageUrl) return;
    // Skip if already loaded
    if (pageImages[pageIndex]) return;
    try {
      const response = await fetch(pageUrl, {
        headers: authHeader ? { Authorization: authHeader } : {},
      });
      if (response.ok) {
        const blob = await response.blob();
        const url = URL.createObjectURL(blob);
        setPageImages((prev) => ({ ...prev, [pageIndex]: url }));
      }
    } catch (err) {
      console.error(`Failed to load page ${pageIndex + 1}:`, err);
    }
  };

  // When current page changes, load the page image
  useEffect(() => {
    if (fileDetail?.url && currentPage >= 1 && currentPage <= selectedPageCount) {
      loadPageImage(fileDetail.url, fileDetail.authorizationHeader, currentPage - 1);
    }
  }, [currentPage, fileDetail, selectedPageCount]);

  // Cleanup blob URLs on unmount or when detail changes
  useEffect(() => {
    return () => {
      Object.values(pageImages).forEach((url) => URL.revokeObjectURL(url));
      Object.values(thumbnailImages).forEach((url) => URL.revokeObjectURL(url));
    };
  }, []);

  // Close download menu on outside click
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (downloadMenuRef.current && !downloadMenuRef.current.contains(e.target)) {
        setShowDownloadMenu(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Sort documents
  const sortedDocuments = useMemo(() => {
    const docs = [...documents];
    if (sortBy === "name-asc") docs.sort((a, b) => (a.name || "").localeCompare(b.name || ""));
    else if (sortBy === "name-desc") docs.sort((a, b) => (b.name || "").localeCompare(a.name || ""));
    else if (sortBy === "date-asc" || sortBy === "date-desc") {
      const getLatestDate = (doc) => {
        const dates = (doc.attachments || []).map((att) => new Date(att.createdDate || 0).getTime());
        return dates.length > 0 ? Math.max(...dates) : 0;
      };
      docs.sort((a, b) => sortBy === "date-asc" ? getLatestDate(a) - getLatestDate(b) : getLatestDate(b) - getLatestDate(a));
    }
    return docs;
  }, [documents, sortBy]);

  // Filter by search
  const filteredDocuments = useMemo(() => {
    let docs = sortedDocuments;

    // My files filter
    if (showMyFiles && currentUserId) {
      docs = docs
        .map((doc) => ({
          ...doc,
          attachments: (doc.attachments || []).filter(
            (att) => att.createdByUserId === currentUserId
          ),
        }))
        .filter((doc) => (doc.attachments || []).length > 0);
    }

    if (!searchTerm.trim()) return docs;
    const term = searchTerm.toLowerCase();
    return docs.filter((doc) => {
      if ((doc.name || "").toLowerCase().includes(term)) return true;
      return (doc.attachments || []).some((att) =>
        (att.name || "").toLowerCase().includes(term)
      );
    });
  }, [sortedDocuments, searchTerm, showMyFiles, currentUserId]);

  const totalFolders = documents.length;

  const toggleFolder = (folderId) => {
    setExpandedFolders((prev) => ({ ...prev, [folderId]: !prev[folderId] }));
  };

  const handleFileClick = (att, folder) => {
    if (selectedFileId === att.id) return;
    setSelectedFileId(att.id);
    setSelectedFileName(att.name || "");
    loadFileDetail(att.id, att.pageCount || 0);
  };

  const totalPages = selectedPageCount;

  const goToPage = (page) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
    }
  };

  const zoomIn = () => {
    const idx = ZOOM_LEVELS.indexOf(zoomLevel);
    if (idx < ZOOM_LEVELS.length - 1) setZoomLevel(ZOOM_LEVELS[idx + 1]);
  };

  const zoomOut = () => {
    const idx = ZOOM_LEVELS.indexOf(zoomLevel);
    if (idx > 0) setZoomLevel(ZOOM_LEVELS[idx - 1]);
  };

  const handleDownload = async (type) => {
    if (!fileDetail?.url) return;
    try {
      let downloadUrl = fileDetail.url;
      let fileName = selectedFileName || fileDetail.name || "document";

      // For PDF download, transform the URL
      if (type === "pdf") {
        const qIndex = downloadUrl.indexOf("?validity");
        if (qIndex !== -1) {
          const basePath = downloadUrl.substring(0, qIndex);
          const queryString = downloadUrl.substring(qIndex);
          downloadUrl = `${basePath}.meta/transformation/version-1.pdf${queryString}`;
        }
        // Ensure filename ends with .pdf
        if (!fileName.toLowerCase().endsWith(".pdf")) {
          fileName = fileName.replace(/\.[^.]+$/, "") + ".pdf";
        }
      }

      const response = await fetch(downloadUrl, {
        headers: fileDetail.authorizationHeader
          ? { Authorization: fileDetail.authorizationHeader }
          : {},
      });
      if (response.ok) {
        const blob = await response.blob();
        const blobUrl = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = blobUrl;
        link.download = fileName;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(blobUrl);
      }
    } catch (err) {
      console.error("Download failed:", err);
    }
    setShowDownloadMenu(false);
  };

  const handlePrint = async () => {
    if (!fileDetail?.url) return;
    try {
      const response = await fetch(fileDetail.url, {
        headers: fileDetail.authorizationHeader
          ? { Authorization: fileDetail.authorizationHeader }
          : {},
      });
      if (!response.ok) return;
      const blob = await response.blob();
      const blobUrl = URL.createObjectURL(blob);
      const printWindow = window.open(blobUrl, "_blank");
      if (printWindow) {
        printWindow.addEventListener("load", () => {
          printWindow.print();
        });
        // Fallback if load event doesn't fire
        setTimeout(() => {
          try { printWindow.print(); } catch (e) { /* already printed */ }
        }, 2000);
      }
    } catch (err) {
      console.error("Print failed:", err);
    }
  };

  const handleFullScreen = () => {
    if (viewerRef.current && viewerRef.current.requestFullscreen) {
      viewerRef.current.requestFullscreen();
    }
  };

  const handlePopOut = () => {
    if (!fileDetail?.url) return;
    window.open(fileDetail.url, "_blank", "noopener,noreferrer");
  };

  if (loading) {
    return (
      <div className="docbeta-loading">
        <RefreshCw size={18} className="docbeta-icon-spin" />
        Loading documents...
      </div>
    );
  }

  return (
    <div className="docbeta-container">
      {/* Left Panel: Folder Tree */}
      <div className="docbeta-sidebar">
        <div className="docbeta-sidebar-header">
          <span className="docbeta-sidebar-title">
            Document Folders | {totalFolders}
          </span>
          <div className="docbeta-sidebar-actions">
            <button
              className={`docbeta-icon-btn ${showMyFiles ? "docbeta-toggle-active" : ""}`}
              onClick={() => setShowMyFiles((p) => !p)}
              title={showMyFiles ? "Show all files" : "Show my files"}
            >
              <User size={14} />
            </button>
            <button
              className="docbeta-icon-btn"
              onClick={() => setShowSearch((p) => !p)}
              title="Search"
            >
              <Search size={14} />
            </button>
            <button className="docbeta-icon-btn" onClick={fetchFolders} title="Refresh">
              <RefreshCw size={14} className={loading ? "docbeta-icon-spin" : ""} />
            </button>
          </div>
        </div>

        {showSearch && (
          <div className="docbeta-search-box">
            <Search size={14} className="docbeta-search-icon" />
            <input
              type="text"
              placeholder="Search folders or files..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="docbeta-search-input"
              autoFocus
            />
            {searchTerm && (
              <button className="docbeta-search-clear" onClick={() => setSearchTerm("")}>
                <X size={12} />
              </button>
            )}
          </div>
        )}

        <div className="docbeta-sort-bar">
          <select
            className="docbeta-sort-select"
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value)}
          >
            <option value="name-asc">Sort: A-Z</option>
            <option value="name-desc">Sort: Z-A</option>
            <option value="date-asc">Sort: Date (Oldest)</option>
            <option value="date-desc">Sort: Date (Newest)</option>
          </select>
        </div>

        <div className="docbeta-folder-list">
          {filteredDocuments.map((doc) => {
            const isExpanded = expandedFolders[doc.id];
            const attCount = (doc.attachments || []).length;

            return (
              <div key={doc.id} className="docbeta-folder-node">
                <div
                  className="docbeta-folder-row"
                  onClick={() => toggleFolder(doc.id)}
                >
                  <span className="docbeta-folder-arrow">
                    {isExpanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                  </span>
                  <span className="docbeta-folder-icon-wrap">
                    {isExpanded ? (
                      <FolderOpen size={16} className="docbeta-folder-icon" />
                    ) : (
                      <FolderClosed size={16} className="docbeta-folder-icon" />
                    )}
                  </span>
                  <span className="docbeta-folder-name">{doc.name}</span>
                  {attCount > 0 ? (
                    <span className="docbeta-folder-badge">{attCount}</span>
                  ) : (
                    <span className="docbeta-folder-badge docbeta-folder-badge-zero">0</span>
                  )}
                </div>
                {isExpanded && (
                  <div className="docbeta-file-list">
                    {attCount > 0 ? (
                      (doc.attachments || []).map((att) => (
                        <div
                          key={att.id}
                          className={`docbeta-file-row ${selectedFileId === att.id ? "active" : ""}`}
                          onClick={() => handleFileClick(att, doc)}
                        >
                          <FileText size={14} className="docbeta-file-icon" />
                          <span className="docbeta-file-name">{att.name}</span>
                        </div>
                      ))
                    ) : (
                      <div className="docbeta-file-row docbeta-empty-folder">
                        <span className="docbeta-file-name">No files</span>
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Right Panel: File Preview */}
      <div className="docbeta-preview-panel">
        {fileLoading ? (
          <div className="docbeta-loading">
            <RefreshCw size={18} className="docbeta-icon-spin" />
            Loading file...
          </div>
        ) : fileDetail ? (
          <>
            {/* Toolbar */}
            <div className="docbeta-toolbar">
              <div className="docbeta-toolbar-left">
                <button
                  className={`docbeta-toolbar-btn ${showThumbnails ? "active" : ""}`}
                  onClick={() => setShowThumbnails((p) => !p)}
                  title={showThumbnails ? "Hide Thumbnails" : "Show Thumbnails"}
                >
                  <Eye size={15} />
                </button>
              </div>
              <div className="docbeta-toolbar-center">
                {/* Page navigation */}
                {totalPages > 0 && (
                  <div className="docbeta-page-nav">
                    <button
                      className="docbeta-page-btn"
                      disabled={currentPage <= 1}
                      onClick={() => goToPage(currentPage - 1)}
                    >
                      <ChevronUp size={14} />
                    </button>
                    <span className="docbeta-page-info">
                      <input
                        type="number"
                        className="docbeta-page-input"
                        value={currentPage}
                        min={1}
                        max={totalPages}
                        onChange={(e) => {
                          const val = parseInt(e.target.value, 10);
                          if (val >= 1 && val <= totalPages) setCurrentPage(val);
                        }}
                      />
                      <span>/ {totalPages}</span>
                    </span>
                    <button
                      className="docbeta-page-btn"
                      disabled={currentPage >= totalPages}
                      onClick={() => goToPage(currentPage + 1)}
                    >
                      <ChevronDown size={14} />
                    </button>
                  </div>
                )}
                {/* Zoom */}
                <div className="docbeta-zoom-controls">
                  <button className="docbeta-toolbar-btn" onClick={zoomOut} disabled={zoomLevel <= ZOOM_LEVELS[0]}>
                    <Minus size={14} />
                  </button>
                  <span className="docbeta-zoom-label">{zoomLevel}%</span>
                  <button className="docbeta-toolbar-btn" onClick={zoomIn} disabled={zoomLevel >= ZOOM_LEVELS[ZOOM_LEVELS.length - 1]}>
                    <Plus size={14} />
                  </button>
                </div>
              </div>
              <div className="docbeta-toolbar-right" ref={downloadMenuRef}>
                <div className="docbeta-download-wrap">
                  <button
                    className="docbeta-toolbar-btn"
                    onClick={() => setShowDownloadMenu((p) => !p)}
                    title="Download"
                  >
                    <Download size={15} />
                    <ChevronDown size={12} />
                  </button>
                  {showDownloadMenu && (
                    <div className="docbeta-download-menu">
                      <div className="docbeta-download-menu-title">Download</div>
                      <button
                        className="docbeta-download-menu-item"
                        onClick={() => handleDownload("original")}
                      >
                        <Download size={14} /> Original File
                      </button>
                      <button
                        className="docbeta-download-menu-item"
                        onClick={() => handleDownload("pdf")}
                      >
                        <Download size={14} /> PDF
                      </button>
                    </div>
                  )}
                </div>
                <button className="docbeta-toolbar-btn" onClick={handlePrint} title="Print">
                  <Printer size={15} />
                </button>
              </div>
            </div>

            {/* Preview Content */}
            <div className="docbeta-preview-body">
              {/* Thumbnails panel */}
              {showThumbnails && totalPages > 0 && (
                <div className="docbeta-thumbnails">
                  {Array.from({ length: totalPages }, (_, index) => (
                    <div
                      key={index}
                      className={`docbeta-thumbnail ${currentPage === index + 1 ? "active" : ""}`}
                      onClick={() => goToPage(index + 1)}
                    >
                      {thumbnailImages[index] ? (
                        <img
                          src={thumbnailImages[index]}
                          alt={`Page ${index + 1}`}
                          className="docbeta-thumbnail-img"
                        />
                      ) : (
                        <div className="docbeta-thumbnail-placeholder">
                          <FileText size={20} />
                        </div>
                      )}
                      <span className="docbeta-thumbnail-num">{index + 1}</span>
                    </div>
                  ))}
                </div>
              )}

              {/* Main viewer */}
              <div className="docbeta-viewer" ref={viewerRef}>
                {pageImages[currentPage - 1] ? (
                  <div className="docbeta-page-container" style={{ transform: `scale(${zoomLevel / 100})` }}>
                    <img
                      src={pageImages[currentPage - 1]}
                      alt={`Page ${currentPage}`}
                      className="docbeta-page-image"
                    />
                  </div>
                ) : (
                  <div className="docbeta-page-loading">
                    <RefreshCw size={18} className="docbeta-icon-spin" />
                    <span>Loading page {currentPage}...</span>
                  </div>
                )}
              </div>
            </div>
          </>
        ) : (
          <div className="docbeta-no-selection">
            <FolderOpen size={48} />
            <p>Select a file from the left panel to preview</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default DocumentFileViewer;
